import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('es');

export default function InvoiceSelect({ value, options, onChange, disabled = false, className = '', 'aria-label': label, ...triggerProps }) {
    const id = useId();
    const triggerRef = useRef(null);
    const popupRef = useRef(null);
    const searchRef = useRef({ text: '', time: 0 });
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const [position, setPosition] = useState({});
    const selected = options.findIndex(option => option.value === value);
    const isDisabled = () => disabled || triggerRef.current?.matches(':disabled') || triggerRef.current?.closest('[hidden]');
    const available = options.map((option, index) => index).filter(index => !options[index].disabled);

    const expand = (last = false) => {
        if (isDisabled() || !available.length) return;
        searchRef.current = { text: '', time: 0 };
        setActive(available.includes(selected) ? selected : last ? available[available.length - 1] : available[0]);
        setOpen(true);
    };
    const choose = index => {
        if (isDisabled() || !options[index] || options[index].disabled) return;
        setOpen(false);
        triggerRef.current.focus({ preventScroll: true });
        if (options[index].value !== value) onChange(options[index].value);
    };

    useLayoutEffect(() => {
        if (!open) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const below = window.innerHeight - rect.bottom - 14;
        const above = rect.top - 14;
        const downward = below >= Math.min(240, above);
        const width = Math.min(Math.max(rect.width, 240), window.innerWidth - 16);
        setPosition({ left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), width,
            maxHeight: Math.max(80, Math.min(280, downward ? below : above)),
            ...(downward ? { top: rect.bottom + 6 } : { bottom: window.innerHeight - rect.top + 6 }) });
    }, [open]);
    useEffect(() => {
        if (!open) return undefined;
        const checkAvailability = () => {
            const trigger = triggerRef.current;
            if (disabled || trigger?.matches(':disabled') || trigger?.closest('[hidden]') || !options.some(option => !option.disabled)) setOpen(false);
            else if (!options[active] || options[active].disabled) setActive(selected >= 0 && !options[selected].disabled ? selected : options.findIndex(option => !option.disabled));
        };
        checkAvailability();
        // The list lives outside the form, so mirror ancestor processing/visibility changes.
        const observer = new MutationObserver(checkAvailability);
        for (let ancestor = triggerRef.current?.parentElement; ancestor; ancestor = ancestor.parentElement) {
            observer.observe(ancestor, { attributes: true, attributeFilter: ['disabled', 'hidden'] });
        }
        return () => observer.disconnect();
    }, [open, disabled, options, active, selected]);
    useEffect(() => {
        if (!open) return undefined;
        const outside = event => {
            if (!triggerRef.current?.contains(event.target) && !popupRef.current?.contains(event.target)) setOpen(false);
        };
        const viewportChanged = event => {
            if (!popupRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('pointerdown', outside, true);
        document.addEventListener('focusin', outside);
        window.addEventListener('scroll', viewportChanged, true);
        window.addEventListener('resize', viewportChanged);
        return () => {
            document.removeEventListener('pointerdown', outside, true);
            document.removeEventListener('focusin', outside);
            window.removeEventListener('scroll', viewportChanged, true);
            window.removeEventListener('resize', viewportChanged);
        };
    }, [open]);
    useEffect(() => {
        if (open) popupRef.current?.querySelector(`[id="${id}-option-${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
    }, [open, active, id]);

    const keyDown = event => {
        if (isDisabled()) return;
        if (event.key === 'Tab') { setOpen(false); return; }
        if (event.key === 'Escape') {
            if (open) { event.preventDefault(); event.stopPropagation(); setOpen(false); }
            return;
        }
        if (['Enter', ' '].includes(event.key)) {
            event.preventDefault();
            if (open) choose(active); else expand();
        } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            if (!available.length) return;
            if (!open) expand(event.key === 'ArrowUp' || event.key === 'End');
            if (event.key === 'Home') setActive(available[0]);
            else if (event.key === 'End') setActive(available[available.length - 1]);
            else if (open) setActive(available[(available.indexOf(active) + (event.key === 'ArrowDown' ? 1 : -1) + available.length) % available.length]);
        } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            const letter = normalize(event.key);
            const previous = open && Date.now() - searchRef.current.time < 700 ? searchRef.current.text : '';
            const text = previous === letter ? letter : previous + letter;
            searchRef.current = { text, time: Date.now() };
            const start = previous && previous !== letter ? active : (open ? active : selected) + 1;
            const match = [...available.filter(index => index >= start), ...available.filter(index => index < start)]
                .find(index => normalize(options[index].label).startsWith(text));
            if (match !== undefined) { setActive(match); setOpen(true); }
        }
    };

    return <div className={`esave-select ${className}`}>
        <button {...triggerProps} type="button" ref={triggerRef} role="combobox" aria-label={label} aria-haspopup="listbox" aria-expanded={open}
            aria-controls={open ? id : undefined} aria-activedescendant={open ? `${id}-option-${active}` : undefined}
            className="esave-input esave-select-trigger" disabled={disabled} onKeyDown={keyDown} onClick={() => open ? setOpen(false) : expand()}>
            <span>{options[selected]?.label || options[0]?.label || 'Selecciona una opción'}</span><ChevronDown size={16} aria-hidden="true" />
        </button>
        {open && createPortal(<div ref={popupRef} id={id} role="listbox" aria-label={label} className="esave-workspace esave-select-popup" style={position}>
            {options.map((option, index) => <div key={option.value} id={`${id}-option-${index}`} role="option" aria-selected={option.value === value}
                aria-disabled={option.disabled || undefined} data-value={option.value} className={`esave-select-option ${index === active ? 'is-active' : ''}`}
                onMouseDown={event => event.preventDefault()} onMouseMove={() => { if (!option.disabled) setActive(index); }} onClick={() => choose(index)}>
                <span>{option.label}</span>{option.value === value && <Check size={16} aria-hidden="true" />}
            </div>)}
        </div>, document.body)}
    </div>;
}
